import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireOperationalAnalytics, fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { createContent } from '@atlassian/embedded-confluence';
import { ContentType, useConfluencePageTree } from '@atlassiansox/confluence-table-tree';
import { useCallback } from 'react';
import { useEmbeddedPage } from '.';
import { createConfluenceDraft } from '../../services/create-confluence-draft';
import { type BlueprintData } from '../../state/ui/blueprints/types';
import { type PageContent } from './types';
import { getInlineOnboardingABCohort } from '../../common/experiments/inline-onboarding/util';
import { inlineOnboardingTemplateJson } from '../../common/experiments/inline-onboarding/template';

export const useCreateEmbeddedPage = ({
	spaceKey,
	projectId,
	projectType,
}: {
	spaceKey: string | null;
	projectId?: number;
	projectType?: string;
}) => {
	const { setEmbeddedPage } = useEmbeddedPage();
	const { insertPage } = useConfluencePageTree();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return useCallback<
		(
			parentContentId: string | undefined,
			isRoot: boolean,
			blueprint?: BlueprintData,
			content?: PageContent,
			templateKey?: string,
		) => Promise<void>
	>(
		async (parentContentId, isRoot, blueprint, content, templateKey) => {
			if (spaceKey) {
				const isUserInInlineOnboardingABVariant = getInlineOnboardingABCohort() === 'variation';
				setEmbeddedPage({
					mode: 'edit',
					isOpen: true,
					isLoading: true,
					page: null,
					pageCreate: {
						error: null,
						parentContentId,
						isRoot,
						blueprint,
					},
				});
				try {
					let contentId;
					let isLive = false;
					let status;
					if (blueprint?.contentBlueprintId) {
						// Create content with panel and checkboxes
						const response = await createConfluenceDraft({
							templateId: blueprint.contentBlueprintId,
							projectSpaceKey: spaceKey,
							projectPageLinkedId: parentContentId || null,
							isEmbeddedPage: true,
							projectId,
							projectType,
							pageTitle: content?.title,
							initialContent: isUserInInlineOnboardingABVariant
								? inlineOnboardingTemplateJson
								: undefined,
						});

						contentId = response.content.id;
						isLive = response.content.subType === 'live';
						status = response.content.status;
					} else {
						// blank page
						let body;
						if (isUserInInlineOnboardingABVariant) {
							body = {
								...inlineOnboardingTemplateJson,
								atlas_doc_format: {
									value: JSON.stringify(inlineOnboardingTemplateJson.atlas_doc_format.value),
									representation: 'atlas_doc_format' as const,
								},
							};
						} else if (content?.body) {
							body = {
								storage: {
									value: content.body,
									representation: 'storage' as const,
								},
							};
						}

						// Use the Atlas doc format for the content
						const response = await createContent({
							spaceKey,
							parentPageId: parentContentId,
							parentProduct: projectType,
							parentProductContentContainerId: projectId?.toString(),
							body: body,
							title: content?.title,
						});
						contentId = response.contentId;
						isLive = response.subType === 'live';
						status = response.status;
					}

					if (contentId) {
						setEmbeddedPage({
							mode: isLive ? 'view' : 'edit',
							page: {
								id: contentId,
								url: `/wiki/spaces/${spaceKey}/pages/${contentId}`, // we don't have a view url yet, so construct a temporary one (that won't work until published)
								editUrl: '',
								hasEmbeddedEdit: true,
								isDraft: status === 'draft',
								isLive,
								type: ContentType.PAGE,
							},
						});
						fireTrackAnalytics(createAnalyticsEvent({}), 'embeddedPageDraft created', {
							template: blueprint?.name || 'blank',
							embeddedPageAction: true,
							hasContent: Boolean(content),
							templateKey: templateKey || 'unknown template key',
							inlineOnboardingExperimentEnabled: isUserInInlineOnboardingABVariant,
						});
						try {
							await insertPage(contentId, parentContentId);
						} catch {
							// ignore error
						}
					} else {
						throw new Error('createContent returned undefined contentId');
					}
				} catch (error) {
					setEmbeddedPage({
						page: null,
						isLoading: false,
						pageCreate: {
							error: error instanceof Error ? error : null,
							parentContentId,
							isRoot,
							blueprint,
						},
					});
					fireOperationalAnalytics(createAnalyticsEvent({}), 'createEmbeddedPageDraft failed', {
						template: blueprint?.name || 'blank',
						embeddedPageAction: true,
						errorMessage: String(error) || 'Unknown error',
						hasContent: Boolean(content),
						templateKey: templateKey || 'Unknown template key',
						inlineOnboardingExperimentEnabled: isUserInInlineOnboardingABVariant,
					});
					return;
				}
			}
		},
		[createAnalyticsEvent, insertPage, setEmbeddedPage, spaceKey, projectId, projectType],
	);
};
